import axiosInstance from "./api";
import { STRIPE_API_URL } from "@/globals/stripe";
import { getDataFastTrackingParams } from "@/utils/datafast";
import { getErrorMessage } from "@/utils/error-message";

/**
 * Stripe checkout / subscription / billing-portal sessions.
 *
 * FETCH, THEN NAVIGATE — and please don't "simplify" this back into a single
 * `window.location.href = <endpoint>`.
 *
 * These endpoints moved into the main backend and now require an Authorization
 * header: the user id comes from the token, never from the query string. (The
 * old service took `?user_id=`, which meant anyone could open anyone else's
 * billing portal by counting.) A top-level navigation cannot carry a header, so
 * the session has to be created by an authenticated XHR that answers
 * `{ url }`, and only then does the browser navigate to that url.
 *
 * The reverse — letting the endpoint 303 straight to Stripe and consuming that
 * over XHR — does not work either: the browser enforces CORS on the redirect
 * hop and Stripe sends no allow-origin header. That is the bug this pattern
 * fixes for the portal, which never worked from the panel.
 */

/** How long to wait before giving up — a hung request must not spin forever. */
const SESSION_TIMEOUT_MS = 20000;

/**
 * Builds the query string, dropping empty values so the backend sees a missing
 * parameter rather than the string "undefined".
 */
function buildQuery(params) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.append(key, String(value));
    }
  });
  return query.toString();
}

/**
 * Attribution parameters. Explicit arguments win; anything not passed falls
 * back to the DataFast cookies, which is where every call site read them from
 * when it was building the URL by hand.
 */
function attributionParams({ datafastVisitorId, datafastSessionId }) {
  const cookies = getDataFastTrackingParams();
  return {
    datafast_visitor_id: datafastVisitorId ?? cookies.datafast_visitor_id,
    datafast_session_id: datafastSessionId ?? cookies.datafast_session_id,
  };
}

/**
 * Creates a session and sends the browser to it.
 *
 * Resolves only once the navigation has been requested; every failure rejects,
 * so callers can clear their spinner in a `catch`. Note that `user_id` is
 * deliberately absent from `params` — the backend reads it from the token and
 * sending it would suggest it still means something.
 */
async function createSessionAndNavigate(path, params) {
  const query = buildQuery(params);
  const url = `${STRIPE_API_URL}${path}${query ? `?${query}` : ""}`;

  const response = await axiosInstance.get(url, { timeout: SESSION_TIMEOUT_MS });
  const sessionUrl = response?.data?.url;

  if (typeof sessionUrl !== "string" || !sessionUrl) {
    throw new Error("Stripe didn't return a payment link. Please try again.");
  }

  window.location.href = sessionUrl;
  return sessionUrl;
}

/** One-off token purchase. */
export function startCheckout({
  productId,
  reference,
  datafastVisitorId,
  datafastSessionId,
} = {}) {
  return createSessionAndNavigate("/checkout", {
    product_id: productId,
    reference,
    ...attributionParams({ datafastVisitorId, datafastSessionId }),
  });
}

/** Recurring plan. `planId` is the Stripe price id (`plan_id_stripe`). */
export function startSubscription({
  planId,
  productId,
  reference,
  datafastVisitorId,
  datafastSessionId,
} = {}) {
  return createSessionAndNavigate("/subscription", {
    plan: planId,
    product_id: productId,
    reference,
    ...attributionParams({ datafastVisitorId, datafastSessionId }),
  });
}

/** Stripe billing portal for the signed-in user's active subscription. */
export function openBillingPortal() {
  return createSessionAndNavigate("/portal", {});
}

/**
 * A message for a failed session, with wording for the statuses these endpoints
 * actually return. Everything else falls through to the shared
 * `getErrorMessage`, which handles the network cases and the plain-text bodies
 * the backend sends.
 */
export function getStripeErrorMessage(error, fallback) {
  const status = error?.response?.status;

  // The interceptor already tried a token refresh before this reached us, so a
  // 401 here means the session is genuinely gone. The raw body ("User must
  // reauthenticate") is not something to show a customer mid-payment.
  if (status === 401) {
    return "Your session has expired. Please sign in again and retry.";
  }
  if (status === 503) {
    return "Payments are temporarily unavailable. Please try again shortly.";
  }

  return getErrorMessage(error, fallback);
}

export default { startCheckout, startSubscription, openBillingPortal, getStripeErrorMessage };
